import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/UserContext';
import authServices from '../../services/AuthServices';

export default function UserEditModal() {
    const { user, setUser } = useAuth();

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Pre-fill when user changes (e.g., after login or update)
    useEffect(() => {
        if (user) {
            setFullName(user.full_name || '');
            setEmail(user.email || '');
            setPhoneNumber(user.phone_number || '');
        }
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email) {
            setError('Email is required.');
            return;
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            setError('Please enter a valid email.');
            return;
        }

        setLoading(true);

        try {
            const details = {
                full_name: fullName || null,
                email,
                phone_number: phoneNumber || null,
            };

            await authServices.updateUser(details);

            setUser({
                ...user,
                full_name: fullName || null,
                email,
                phone_number: phoneNumber || null,
            });

            toast.success('Profile updated successfully!');

            const modalToggle = document.getElementById('user-update-modal');
            if (modalToggle) modalToggle.checked = false;

        } catch (err) {
            console.error('Profile update error:', err);

            let errorMessage = 'Failed to update profile';

            if (err.response?.data?.details) {
                errorMessage = err.response.data.details
                    .map(d => d.message)
                    .filter(Boolean)
                    .join('; ') || 'Validation failed';
            } else if (err.response?.data?.detail) {
                errorMessage = err.response.data.detail;
            } else if (err.response?.status === 401) {
                errorMessage = 'Session expired. Please log in again.';
            } else if (err.request) {
                errorMessage = 'Network error — please check your connection';
            } else {
                errorMessage = err.message || 'Something went wrong';
            }

            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {/* DaisyUI modal toggle — controlled externally via htmlFor */}
            <input type="checkbox" id="user-update-modal" className="modal-toggle" />

            <div className="modal" role="dialog">
                <div className="modal-box w-11/12 max-w-lg">
                    <label
                        htmlFor="user-update-modal"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                        aria-label="Close"
                    >
                        ✕
                    </label>

                    <h3 className="text-lg font-bold mb-4">Edit Profile Details</h3>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className='form-control'>
                            <label
                                htmlFor="full_name"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                Full Name
                            </label>
                            <input
                                type="text"
                                id="full_name"
                                className="input input-bordered w-full mt-1 border p-2 rounded-md border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="e.g., John Doe"
                            />
                        </div>

                        <div className='form-control'>
                            <label
                                htmlFor="email"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                Email *
                            </label>
                            <input
                                type="email"
                                id="email"
                                className="input input-bordered w-full mt-1 border p-2 rounded-md border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>

                        <div className='form-control'>
                            <label
                                htmlFor="phone_number"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                id="phone_number"
                                className="input input-bordered w-full mt-1 border p-2 rounded-md border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder="+254 7XX XXX XXX"
                            />
                        </div>

                        <div>
                            <button
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Backdrop (DaisyUI requires this for close-on-click) */}
                <label className="modal-backdrop" htmlFor="user-update-modal">
                    Close
                </label>
            </div>
        </div>
    );
}